import { withAlpha } from '../common/withAlpha';
import environments from '../environments';
import { OrganizationConfig } from '../models/Organization';

const defaultColors = {
  text: '#4F4F4F',
  textSecondary: '#767676',
  textDisabled: '#d1d1d1',
  primary: environments.theme?.colors?.primary || '#2BAB2B',
  primaryDark: environments.theme?.colors?.primaryDark || '#1C691C',
  primaryLight: environments.theme?.colors?.primaryLight || '#30C730',
  contrast: '#FFF',
  primaryContainedHoverBackground: '#1b6f1c',
  primaryOutlinedHoverBackground: '#eef8ee',
  primaryOutlinedRestingBorder:
    environments.theme?.colors?.primaryOutlineRestingBorder || '#94d494',
  secondary: environments.theme?.colors?.secondary || '#41586B',
  accent:
    environments.theme?.colors?.accent ||
    environments.theme?.colors?.secondary ||
    environments.theme?.colors?.primary ||
    '#2BAB2B',
  secondaryDark: '#2D3D4A',
  secondaryLight: '#677988',
  secondaryContainedHoverBackground: '#2e3e4b',
  secondaryOutlinedHoverBackground: '#f0f2f4',
  secondaryOutlinedRestingBorder: '#9fabb4',
  actionActive: '#7a8b98',
  actionHover: '#f8f9f9',
  actionSelected: '#f0f2f4',
  actionDisabled: '#ced4d8',
  actionDisabledBackground: '#e7ebed',
  actionFocus: '#e7ebed',
  otherDivider: '#e7ebed',
  otherOutlineBorder: '#FFF',
  otherStandardInputLine: '#FFF',
  otherBackdropOverlay: '#9fabb4',
  otherRatingActive: '#FF7700',
  otherSnackbarBackground: '#41586B',
  error: '#F44336',
  errorDark: '#E31B0C',
  errorLight: '#F88078',
  errorContrast: '#FFF',
  errorContainedHoverBackground: '#ab2e25',
  errorOutlinedHoverBackground: '#ffefef',
  errorOutlinedRestingBorder: '#f9a09a',
  errorAlertContent: '#621b16',
  errorAlertBackground: '#fdebe9',
  warning: '#FF7700',
  warningDark: '#B25300',
  warningLight: '#FF9233',
  warningAlertBackground: '#FFF4C3',
  info: '#2196F3',
  infoDark: '#0B79D0',
  infoLight: '#64B6F7',
  infoContrast: '#FFF',
  infoContainedHoverBackground: '#1768aa',
  infoOutlinedRestingBorder: '#8fcbf8',
  infoOutlinedHoverBackground: '#edf7fe',
  infoAlertContent: '#0d3d61',
  infoAlertBackground: '#eaf4fc',
  success: '#217E0E',
  successAlertBackground: '#CDFFC3',
  default: '#FAFAFA',
  green: '#30C730',
  grey50: '#FAFAFA',
  grey100: '#F5F5F5',
  grey300: '#E0E0E0',
  grey400: '#BDBDBD',
  grey600: '#757575',
  grey700: '#616161',
  grey900: '#212121',
  purple2: '#BB6BD9',
  headerBackground: '#5E51AC',
  profileMenuBackground: '#8C538A',
  blackTint: '#4d4d4d',
  lightText: '#fff',
  darkText: '#170F2A',
  mediumText: '#000000',
  link: environments.theme?.colors?.link || '#1C691C',
  divider: '#e4e4e4',
  formLabel: '#4d4d4d',
  formPlaceholder: '#000000',
  formInputBorder: '#ddd',
  formInputBorderHighlight: '#ccc',
  checked: '#ff5286',
  switchTrackColor: '#8086ff78',
  switchActiveThumbColor: '#9C86FF',
  placeholderColor: '#BBB'
};

const colors = { ...defaultColors };

export default colors;

let themeVersion = 0;
const themeListeners = new Set<() => void>();

export const getThemeVersion = () => themeVersion;

export const subscribeToThemeChanges = (listener: () => void) => {
  themeListeners.add(listener);
  return () => {
    themeListeners.delete(listener);
  };
};

const notifyThemeChanged = () => {
  themeVersion += 1;
  themeListeners.forEach(listener => listener());
};

/**
 * Allows overriding color theme with organization configuration
 * @param config
 */
export const applyOrganizationTheme = (config: OrganizationConfig) => {
  Object.assign(colors, {
    primary: config.primaryColor || defaultColors.primary,
    primaryDark: config.primaryColor || defaultColors.primaryDark,
    primaryLight: config.primaryColor || defaultColors.primaryLight,
    secondary: config.secondaryColor || defaultColors.secondary,
    accent:
      config.secondaryColor || config.primaryColor || defaultColors.accent,
    link: config.primaryColor || defaultColors.link,
    primaryOutlinedRestingBorder: withAlpha(
      config.primaryColor || defaultColors.primary,
      0.2
    )
  });
  notifyThemeChanged();
};

export const resetOrganizationTheme = () => {
  Object.assign(colors, defaultColors);
  notifyThemeChanged();
};
